import { Component, OnInit, Inject, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { map, switchMap } from "rxjs";
import { CrudService } from "src/app/services/crud.service";
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeleteConfirmationComponent } from "src/app/layout/modals/delete-confirmation/delete-confirmation.component";

@Component({
  selector: "app-create-transcription",
  templateUrl: "./create-transcription.component.html",
  styleUrl: "./create-transcription.component.css",
})
export class CreateTranscriptionComponent implements OnInit {
  @ViewChild('deleteDialogRef') deleteDialogRef: DeleteConfirmationComponent;

  transcriptionForm: FormGroup;
  submitted = false;
  file: File | null = null;
  spinner: boolean = false;

  subClientIds: string[] = [];
  mainVideoIds: string[] = [];
  mainVideoNames: string[] = [];

  filteredLists: { [key: string]: string[] } = {
    subClientId: [],
    mainVideoId: [],
    mainVideoName: [],
  };

  // Dropdown visibility flags stored by control name
  showDropdowns: { [key: string]: boolean } = {
    subClientId: false,
    mainVideoId: false,
    mainVideoName: false,
  };

  transcriptionData: any;
  

  constructor(
    private fb: FormBuilder,
    private toaster: ToastrService,
    public apiService: CrudService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreateTranscriptionComponent>
  ) {
    console.log("Data ==> ", this.data)
    this.transcriptionForm = this.fb.group({
      subClientId: [
        "",
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(25),
        ],
      ],
      mainVideoId: [
        "",
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(25),
        ],
      ],
      mainVideoName: ["", Validators.required],
      videoId: [
        "",
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(25),
        ],
      ],
      videoName: ["", Validators.required],
      isRestricted: [true],
      file: [null, Validators.required],
    });

    if(this.data){
      this.transcriptionData = this.data;
      this.transcriptionForm.patchValue({
        subClientId: this.transcriptionData.subClientId,
        mainVideoId: this.transcriptionData.mainVideoId,
        mainVideoName: this.transcriptionData.mainVideoName,
        videoId: this.transcriptionData.videoId,
        videoName: this.transcriptionData.videoName,
        isRestricted: this.transcriptionData.isRestricted,
    });
    }

  }

  ngOnInit() {
    this.getTranscriptionDocumentsIds();
  }

  get formControls() {
    return this.transcriptionForm.controls;
  }

  getTranscriptionDocumentsIds() {
    this.apiService.getTranscriptionDocumentsIds().subscribe((res) => { 
      if (res && res.data && res.data.data) {
        this.subClientIds = res.data.data.subClientIds || [];
        this.mainVideoIds = res.data.data.mainVideoIds || [];
        this.mainVideoNames = res.data.data.mainVideoNames || [];
      }
    }, (err) => {
      this.toaster.error("Failed to fetch transcription documents IDs", err);
    });
  }

  onFileChange(event: any) {
    this.file = event.target.files[0];

    // Validate file type
    const allowedTypes = ["text/vtt", "text/csv"];
    const validExtensions = ["vtt", "csv"];
    const ext = this.file.name.split(".").pop()?.toLowerCase();

    if (
      !allowedTypes.includes(this.file.type) &&
      !validExtensions.includes(ext || "")
    ) {
      this.spinner = false;
      this.toaster.error("Only VTT and CSV files are allowed!");
      return;
    }
    if (this.file) {
      this.transcriptionForm.patchValue({ file: this.file });
      this.transcriptionForm.get("file")?.updateValueAndValidity();
    }
  }

  onSubmit() {
    this.submitted = true;

    console.log('this.file: ', this.file);
    if (this.transcriptionForm.invalid) {
      return;
    }

    this.spinner = true;

    const payloadObj = {
      name: this.file.name,
      type: this.file.type,
      URL: null,
      duration: 0,
      thumbnail: "",
      size: this.file.size,
      originalName: this.file.name,
      isAIEditorVideo: false,
    };

    this.apiService.createpresignedUrlV2(payloadObj).pipe(
      switchMap((presignedUrlResponse) => {
        return this.apiService.uploadTranscriptionFile(presignedUrlResponse.data.details, this.file).pipe(
          map(() => presignedUrlResponse)
        );
      }),
      switchMap((uploadResponse) => {
        const formValue = { ...this.transcriptionForm.value };
        delete formValue.file;

        const payload = {
          ...formValue,
          subtitleUrl: uploadResponse.data.details.split('?')[0],
          uploadVideoID: uploadResponse.data.uploadVideoId,
          status: "Pending"
        };
        return this.apiService.createTranscription(payload);
      }),
      switchMap((createTranscriptionResponse) => { 
        return this.apiService.ProcessVttTranscription({ id: createTranscriptionResponse.data.data._id });
      })
    ).subscribe({
      next: (parseAIResponse) => {
        // this.toaster.success("Transcription created successfully!");
        this.spinner = false;
        this.toaster.info(`Your transcription is being processed.`);
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.log('err: ', err);
        this.spinner = false;
        this.toaster.error("An error occurred", err);
      }
    });
    
  }

  closeModal() {
    this.dialogRef.close();
  }

  onInputChange(controlName: string, sourceList: string[]) {
    const val = this.transcriptionForm.get(controlName)?.value?.toLowerCase() || "";
    this.filteredLists[controlName] = sourceList.filter((item) =>
      item?.toLowerCase().includes(val)
    );
    this.showDropdowns[controlName] =
      this.filteredLists[controlName].length > 0;
  }  

  selectItem(controlName: string, value: string) {
    this.transcriptionForm.patchValue({ [controlName]: value });
    this.showDropdowns[controlName] = false;
  }

  hideDropdownWithDelay(controlName: string) {
    setTimeout(() => {
      this.showDropdowns[controlName] = false;
    }, 200);
  }

  showDropdown(controlName: string) {
    // Show all items from the full list on focus, ignoring the input value
    switch (controlName) {
      case 'subClientId':
        this.filteredLists[controlName] = [...this.subClientIds];
        break;
      case 'mainVideoId':
        this.filteredLists[controlName] = [...this.mainVideoIds];
        break;
      case 'mainVideoName':
        this.filteredLists[controlName] = [...this.mainVideoNames];
        break;
    }
    this.showDropdowns[controlName] = this.filteredLists[controlName].length > 0;
  }
  
  openFile() {
    window.open(this.transcriptionData.subtitleUrl, '_blank');
  }

  getContentTypeFromUrl(url: string): string | null {
    if (!url) return null;
    try {
      const path = new URL(url).pathname;
      if (path.endsWith('.vtt')) {
        return 'text/vtt';
      } else if (path.endsWith('.csv')) {
        return 'text/csv';
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  clearImg(){
    this.transcriptionForm.get('file')?.setValue(null);
  }

  openConfirmationDelete(item: any) {
    this.deleteDialogRef.open({
        header: `Delete Badge`,
        title: `Are you sure you want to delete this transcription?`
    }, (e) => {
        if (e) {
          this.spinner = true;
          this.apiService.deleteTranscriptions({ids: [item._id]}).subscribe((success) => {
                this.toaster.success(`Transcription deleted successfully !!`);
                this.dialogRef.close(true);
            }).add(() => this.spinner = false);
        }
    });
  }
  
}
